/**
 * CR-6446: supplier-view (SO Headers) sections of the headers filter panel —
 * Company, Supplier and Order Details, below the shared Date Range section.
 *
 * Input types are preserved exactly as the ticket requires.
 */
import { Building2, Truck, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField, filterControlClass } from '../../../filters/FilterSection';
import { SectionDivider, STATUS_OPTIONS, POHeaderPanelProps } from './poHeaderFilterShared';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

export function SOHeaderFilterPanel({
  filters,
  textInputs,
  siteOptions,
  warehouseOptions,
  divisionOptions,
  siteCountries,
  supplierGroupOptions,
  supplierSiteOptions,
  handlers,
  onFilterChange,
  onApply,
}: POHeaderPanelProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  const {
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierGroupChange,
  } = handlers;

  return (
    <>
      {/* COMPANY section */}
      <FilterSection title="Company" icon={Building2}>
        <FilterGrid>
          <FilterField label="Site">
            <SearchableSelect
              options={siteOptions}
              value={filters.site}
              onChange={(val) => handleSiteChange(val)}
              placeholder="All Company Sites"
            />
          </FilterField>
          {fv.division && (
          <FilterField label="Division">
            <select
              value={filters.division}
              onChange={(e) => handleDivisionChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Divisions</option>
              {divisionOptions.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </FilterField>
          )}
          {fv.companyCountry && (
          <FilterField label="Country">
            <select
              value={filters.companyCountry}
              onChange={(e) => handleCompanyCountryChange(e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Countries</option>
              {siteCountries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </FilterField>
          )}
          {fv.warehouse && (
          <FilterField label="Warehouse">
            <select
              value={filters.warehouse}
              onChange={(e) => onFilterChange('warehouse', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((w) => (
                <option key={w.value} value={w.value}>{w.label}</option>
              ))}
            </select>
          </FilterField>
          )}
        </FilterGrid>
      </FilterSection>

      <SectionDivider />

      {/* SUPPLIER section */}
      <FilterSection title="Supplier" icon={Truck}>
        <FilterGrid>
          <FilterField label="Group">
            <SearchableSelect
              options={supplierGroupOptions}
              value={filters.supplierGroup}
              onChange={(val) => handleSupplierGroupChange(val)}
              placeholder="All Supplier Groups"
            />
          </FilterField>
          <FilterField label="Site">
            <SearchableSelect
              options={supplierSiteOptions}
              value={textInputs.supplier}
              onChange={(val) => onFilterChange('supplier', val)}
              placeholder="All Supplier Sites"
            />
          </FilterField>
        </FilterGrid>
        {/* CR-6446: no "Strategic suppliers only" tickbox here. The design's
            Supplier section is Group and Site only, and on the supplier
            portal the flag is a property of the viewer's own company —
            the same reasoning that dropped it from the supplier NCR panel
            (CR-6451). The customer PO Headers view keeps it. */}
      </FilterSection>

      <SectionDivider />

      {/* ORDER DETAILS section */}
      <FilterSection title="Order Details" icon={FileText}>
        <FilterGrid>
          <FilterField label="SO Number">
            <input
              type="text"
              placeholder="Enter SO Number..."
              value={textInputs.poNumber}
              onChange={(e) => onFilterChange('poNumber', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onApply?.(); }}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Status">
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField label="PDF Version Attached">
            <select
              value={filters.pdfAttached}
              onChange={(e) => onFilterChange('pdfAttached', e.target.value)}
              className={filterControlClass}
            >
              <option value="">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </FilterField>
        </FilterGrid>
      </FilterSection>
    </>
  );
}
